import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MapPin, Users, CheckCircle2, ArrowRight } from 'lucide-react';
import Button from '../ui/Button';
import { Badge } from '../ui/Badge';

export default function EventCard({ event, index = 0, onRegister }) {
  const date = parseISO(event.date);
  const remaining = event.totalSlots - event.filledSlots;
  const soldOut = remaining <= 0;
  const pct = Math.min(100, Math.round((event.filledSlots / event.totalSlots) * 100));
  const [registered, setRegistered] = useState(false);

  return (
    <article
      className="animate-fade-up overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft opacity-0"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
      data-testid={`event-card-${event.id}`}
    >
      <Link to={`/evento/${event.id}`} className="group block">
        <div className="relative h-36 w-full">
          <img src={event.cover} alt={event.title} loading="lazy" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute left-4 top-4 flex flex-col items-center rounded-xl bg-white/95 px-3 py-1.5 shadow-soft">
            <span className="font-heading text-lg font-extrabold leading-none text-primary">
              {format(date, 'dd')}
            </span>
            <span className="text-[11px] font-semibold uppercase text-ink-soft">
              {format(date, 'MMM', { locale: ptBR })}
            </span>
          </div>
          <div className="absolute right-4 top-4">
            {soldOut ? (
              <Badge tone="danger">Esgotado</Badge>
            ) : (
              <Badge tone="success">Inscrições abertas</Badge>
            )}
          </div>
        </div>

        <div className="p-4">
          <h3 className="font-heading text-lg font-bold leading-snug text-ink transition-colors group-hover:text-primary">
            {event.title}
          </h3>
          <p className="mt-1 text-sm text-ink-soft">
            {format(date, "EEEE, dd 'de' MMMM · HH:mm", { locale: ptBR })}
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-ink-soft">
            <MapPin size={15} className="text-ink-faint" /> {event.location}
          </p>

          <div className="mt-3">
            <div className="flex items-center justify-between text-xs font-semibold text-ink-soft">
              <span className="inline-flex items-center gap-1.5">
                <Users size={14} /> {event.filledSlots}/{event.totalSlots} vagas
              </span>
              <span>{soldOut ? 'Lotado' : `${remaining} restantes`}</span>
            </div>
            <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className={`h-full rounded-full ${soldOut ? 'bg-rose-400' : 'bg-primary'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-colors group-hover:text-primary-dark">
            Ver detalhes <ArrowRight size={15} />
          </span>
        </div>
      </Link>

      <div className="px-4 pb-4">
        {registered ? (
          <Button variant="outline" className="w-full" disabled data-testid={`event-registered-${event.id}`}>
            <CheckCircle2 size={17} className="text-emerald-500" /> Inscrição confirmada
          </Button>
        ) : (
          <Button
            variant={soldOut ? 'outline' : 'accent'}
            className="w-full"
            disabled={soldOut}
            onClick={() => onRegister(event, () => setRegistered(true))}
            data-testid={`event-register-${event.id}`}
          >
            {soldOut ? 'Vagas esgotadas' : 'Fazer inscrição'}
          </Button>
        )}
      </div>
    </article>
  );
}


